import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoABlock,
  GoACallout,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAInput,
  GoAInputText,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import {
  Collision,
  defaultVehicle,
  handleNestedChange,
  Vehicle,
} from '../../models/collision';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  updateCollision,
  validateVehicle,
} from '../../state/slices/collisionSlice';
import useCsrService from '../../services/csr-api-redux';

import styles from './driver-vehicle.module.css';
import { formatPhoneNumber } from '../../utils/formUtils';
import { isHitAndRun } from '../../state/slices/configurationSlice';
import { displaySection } from '../../common/ui-library';
import { setError } from '../../state/slices/agreementSlice';

interface VehicleComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

const defaultProvince = 'AB';
const defaultGender = '3';
export const VehicleComponent = ({
  navigationConfiguration,
}: VehicleComponentProps) => {
  const dispatch = useAppDispatch();
  const csrService = useCsrService();
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [isEcollisionInvalid, setIsEcollisionInvalid] = useState(false);
  const [errors, setErrors] = useState<
    {
      key: string;
      value: string;
    }[]
  >([]);

  const [vehicle, setVehicle] = useState<Vehicle>(
    stateCollision.collision.vehicles
      ? stateCollision.collision.vehicles[0]
      : defaultVehicle,
  );

  useEffect(() => {
    let pageConfig = 'error';

    if (
      isHitAndRun(stateCollision.collision.collisionType!, stateConfiguration)
    ) {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.hitandrun !== undefined,
      )!.hitandrun;
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined,
      )!.default;
    }

    setNextPage(pageConfig);
  }, []);

  const [nextPage, setNextPage] = useState('');

  const validate = async (): Promise<boolean> => {
    let valid = true;
    const newErrors: {
      key: string;
      value: string;
    }[] = [];

    dispatch(removeAllValidationErrorNotifications());
    /*
      TODO: Verify with business what regex/rules need to be for plate numbers
      //most common
      3 characters - 3 characters
      3 characters - 4 characters

      //also handle custom plates (rules??)
    */
    const licencePlateRegex =
      /^(?:[A-Za-z0-9]{3}-[A-Za-z0-9]{3,4}|[A-Za-z0-9 -]{1,7})$/;
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
    const yearRegex = /^(19[1-9][6-9]|19[2-9][0-9]|20[0-9]{2}|9[0-9]{3})$/;
    const damageRegex = /^\d+(\.\d{1,2})?$/;
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/;
    const phoneRegex = /^\d{10}$/;

    setIsEcollisionInvalid(false);

    if (!vehicle.licencePlateNumber) {
      newErrors.push({
        key: 'licencePlateNumber',
        value: 'Enter a licence plate number',
      });
      valid = false;
    } else if (!licencePlateRegex.test(vehicle.licencePlateNumber)) {
      newErrors.push({
        key: 'licencePlateNumber',
        value: 'Enter a valid licence plate number (for example A1B-2C3)',
      });
      valid = false;
    }

    if (!vehicle.vinNumber) {
      newErrors.push({
        key: 'vinNumber',
        value: 'VIN number is required',
      });
      valid = false;
    } else if (!vinRegex.test(vehicle.vinNumber)) {
      newErrors.push({
        key: 'vinNumber',
        value: 'Enter a valid VIN number (17 characters)',
      });
      valid = false;
    }

    if (!vehicle.year) {
      newErrors.push({
        key: 'year',
        value: 'Year is required',
      });
      valid = false;
    } else {
      const currentYear = new Date().getFullYear();
      const maxYear = Math.max(currentYear + 2);

      const yearValid =
        (yearRegex.test(vehicle.year) &&
          parseInt(vehicle.year, 10) <= maxYear) ||
        vehicle.year == '9999';

      if (!yearValid) {
        newErrors.push({
          key: 'year',
          value: 'Enter a valid year',
        });

        valid = false;
      }
    }

    if (!vehicle.make) {
      newErrors.push({
        key: 'make',
        value: 'Make is required',
      });
      valid = false;
    }
    if (!vehicle.model) {
      newErrors.push({
        key: 'model',
        value: 'Model is required',
      });
      valid = false;
    }
    if (!vehicle.type) {
      newErrors.push({
        key: 'type',
        value: 'Vehicle type is required',
      });
      valid = false;
    }
    if (!vehicle.color) {
      newErrors.push({
        key: 'color',
        value: 'Vehicle colour is required',
      });
      valid = false;
    }

    if (
      vehicle.hasObtainedDamageEstimate === undefined ||
      vehicle.hasObtainedDamageEstimate === null
    ) {
      newErrors.push({
        key: 'hasObtainedDamageEstimate',
        value: 'Damage estimate is required',
      });
      valid = false;
    }

    if (vehicle.hasObtainedDamageEstimate === true) {
      console.log('vehicle.damageEstimate');
      if (!vehicle.damageEstimate || vehicle.damageEstimate < 0) {
        newErrors.push({
          key: 'damageEstimate',
          value: 'Enter a damage estimate',
        });
        valid = false;
      } else if (!damageRegex.test(vehicle.damageEstimate.toString())) {
        newErrors.push({
          key: 'damageEstimate',
          value: 'Enter a valid damage estimate',
        });
        valid = false;
      }
    }

    if (!vehicle.insuredBy?.companyName) {
      newErrors.push({
        key: 'companyName',
        value: 'Insurance company name is required',
      });
      valid = false;
    }
    if (!vehicle.insuredBy?.insurancePolicyNumber) {
      newErrors.push({
        key: 'insurancePolicyNumber',
        value: 'Insurance policy number is required',
      });
      valid = false;
    }
    if (!vehicle.insuredBy?.insurancePolicyExpirationDate) {
      newErrors.push({
        key: 'insurancePolicyExpirationDate',
        value: 'Insurance policy expiry date is required',
      });
      valid = false;
    }

    console.log(vehicle.isOwner);

    if (vehicle.isOwner === undefined || vehicle.isOwner === null) {
      newErrors.push({
        key: 'isOwner',
        value: 'Ownership is required',
      });
      valid = false;
    }

    if (vehicle.isOwner === false) {
      if (!vehicle.owner?.firstName) {
        newErrors.push({
          key: 'ownerFirstName',
          value: "Owner's first name is required",
        });
        valid = false;
      }
      if (!vehicle.owner?.lastName) {
        newErrors.push({
          key: 'ownerLastName',
          value: "Owner's last name is required",
        });
        valid = false;
      }
      if (!vehicle.owner?.address?.address) {
        newErrors.push({
          key: 'ownerStreetAddress',
          value: "Owner's street address is required",
        });
        valid = false;
      }
      if (!vehicle.owner?.address?.city) {
        newErrors.push({
          key: 'ownerCity',
          value: "Owner's city is required",
        });
        valid = false;
      }

      if (!vehicle.owner?.address?.postalCode) {
        newErrors.push({
          key: 'ownerPostalCode',
          value: "Owner's postal code is required",
        });
        valid = false;
      } else {
        if (
          !postalCodeRegex.test(
            vehicle.owner?.address?.postalCode.replace(' ', ''),
          )
        ) {
          newErrors.push({
            key: 'ownerPostalCode',
            value: 'Postal code is invalid, use (A1A 1A1 or A1A1A1) format',
          });
          valid = false;
        }
      }
      if (!vehicle.owner?.homePhoneNumber) {
        newErrors.push({
          key: 'ownerPrimaryPhone',
          value: "Owner's primary phone number is required",
        });
        valid = false;
      } else {
        // Remove all non-digit characters
        const cleaned = vehicle.owner.homePhoneNumber.replace(/[^\d]/g, '');
        // Check if the cleaned string contains exactly 10 digits
        const validPhone = phoneRegex.test(cleaned);
        if (!validPhone) {
          newErrors.push({
            key: 'ownerPrimaryPhone',
            value: 'Primary phone number is invalid',
          });
          valid = false;
        }
      }

      if (vehicle.owner?.workPhoneNumber) {
        const cleaned = vehicle.owner.workPhoneNumber.replace(/[^\d]/g, '');
        // Check if the cleaned string contains exactly 10 digits
        const validPhone = phoneRegex.test(cleaned);
        if (!validPhone) {
          newErrors.push({
            key: 'ownerAltPhone',
            value: 'Alt phone number is invalid',
          });
          valid = false;
        }
      }
    }

    //finished doing client-side validation
    if (!handleValidationErrors(newErrors)) {
      return false; // Stop further validation if there are errors
    }

    //begin server-side validation against eCollision

    //default eCollision error message
    let eCollisionMessage =
      "The information below doesn't match our database. Please confirm it is correct and try again.";
    try {
      const result = await dispatch(
        validateVehicle({ csrService, collision: updatedCollisionWithVehicle }),
      ).unwrap();

      console.log(result);
      //validity is based on response from eCollision
      valid = result.payload.isValid;

      const newIsPlateExpired = !!result.payload?.isExpired;
      const newIsPlateLostOrStolen = !!result.payload?.isLostOrStolen;
      const newDoesVehicleModelMatchWithMOVEs =
        !!result.payload?.doesVehicleModelMatchWithMoves;
      const newModelNameFromMOVEs = result.payload?.model;

      //Updating the state here as the handleNestedChange is fired late due to the async call
      updatedCollisionWithVehicle.vehicles[0].isPlateExpired =
        newIsPlateExpired;
      updatedCollisionWithVehicle.vehicles[0].isPlateLostOrStolen =
        newIsPlateLostOrStolen;
      updatedCollisionWithVehicle.vehicles[0].doesVehicleModelMatchWithMOVEs =
        newDoesVehicleModelMatchWithMOVEs;
      updatedCollisionWithVehicle.vehicles[0].modelNameFromMOVEs =
        newModelNameFromMOVEs;

      setIsEcollisionInvalid(!valid);
      eCollisionMessage =
        "Some vehicle details don't match our records. Please review the highlighted fields and ensure they are entered correctly before proceeding.";

      console.log('ISVALID', valid);
      if (!valid) {
        newErrors.push({
          key: 'licencePlateNumber',
          value: 'Confirm licence plate number is correct',
        });
        newErrors.push({
          key: 'vinNumber',
          value: 'Confirm VIN is correct',
        });
        newErrors.push({
          key: 'year',
          value: 'Confirm year is correct',
        });
        newErrors.push({
          key: 'make',
          value: 'Confirm make is correct',
        });
        //Commenting this out as we don't validate model against MOVEs https://goa-dio.atlassian.net/browse/CSR-2646
        // newErrors.push({
        //   key: 'model',
        //   value: 'Confirm model is correct',
        // });
      }
      // console.log("isExpired? (local variable):", newIsPlateExpired);
    } catch (error) {
      console.log(error);
      eCollisionMessage = 'An error occurred during vehicle validation.';
      valid = false;
    }

    console.log('ISVALID', valid);

    if (!valid && !handleValidationErrors(newErrors, eCollisionMessage)) {
      return false; // Stop further data-entry if there are any moves validation errors
    }

    return valid;
  };

  const handleValidationErrors = (
    errors: { key: string; value: string }[],
    bannerMessage?: string,
  ) => {
    let notificationMessage = 'Fill out the required information below.'; // default notification message

    // If there are errors, record them
    if (errors.length > 0) {
      setErrors(errors);
    } else {
      setErrors([]);
    }

    // Decide which message to display
    if (bannerMessage && bannerMessage.length > 0) {
      notificationMessage = bannerMessage;
    }

    // If no errors and no custom message, validation passes silently
    if (errors.length === 0 && (!bannerMessage || bannerMessage.length === 0)) {
      return true;
    }

    // If we have either errors or a custom message (or both), show notification and return false
    dispatch(
      pushNotification({
        body: notificationMessage,
        notificationType: NotificationType.importantValidationError,
      }),
    );

    return false;
  };

  const updatedCollisionWithVehicle = {
    ...stateCollision.collision,
    vehicles: [
      {
        ...stateCollision.collision.vehicles?.[0],
        ...vehicle,
        licencePlateProvince: defaultProvince,
      },
    ],
  };

  const save = () => {
    if (!vehicle.isOwner) {
      const owner = {
        ...vehicle.owner!,
        gender: defaultGender,
        address: {
          ...vehicle.owner!.address,
          province: defaultProvince,
        },
      };

      updatedCollisionWithVehicle.vehicles[0].owner = owner;
    }

    let updatedCollision: Collision = updatedCollisionWithVehicle;

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const handleCheckErrors = (fieldName: string): string => {
    const error = errors.find((x) => x.key == fieldName);

    if (error) {
      return error.value;
    }

    return '';
  };

  const getIsOwner = (isOwner: boolean | undefined) => {
    if (isOwner === undefined) {
      return '';
    }

    return isOwner ? 'Yes' : 'No';
  };

  return (
    <div>
      <div className={styles.container}>
        <div>
          <h1>Driver and vehicle information</h1>
          <p>
            Please enter vehicle information below. The vehicle must be
            registered in Alberta (have an Alberta licence plate) to continue.
          </p>
        </div>

        <div className={styles.formelements}>
          <VehicleInformationComponent
            vehicle={vehicle}
            isEcollisionInvalid={isEcollisionInvalid}
            stateUpdate={setVehicle}
            onChange={handleNestedChange}
            checkError={handleCheckErrors}
          />
          <GoASpacer vSpacing={'l'} />

          <div className="large-body">Owner information</div>
          <GoASpacer vSpacing={'m'} />
          <GoAFormItem
            label="Are you the registered owner of this vehicle?"
            error={handleCheckErrors('isOwner')}
          >
            <GoARadioGroup
              name="owner"
              value={getIsOwner(vehicle.isOwner ?? undefined)}
              error={handleCheckErrors('isOwner').length > 0}
              onChange={(_, value) => {
                const isOwner = value.toLowerCase() === 'yes';
                handleNestedChange(['isOwner'], isOwner, setVehicle);

                // Clear owner details if the user is the owner
                if (isOwner) {
                  handleNestedChange(['owner'], null, setVehicle);
                }
              }}
            >
              <GoARadioItem value="Yes" />
              <GoARadioItem value="No" />
            </GoARadioGroup>
          </GoAFormItem>
        </div>
        <div
          style={{
            display:
              vehicle.isOwner == undefined || vehicle.isOwner
                ? 'none'
                : 'block',
          }}
        >
          <div>
            <GoACallout type="information">
              The information below can be found on the vehicle's registration
              slip.
            </GoACallout>
          </div>
          <div className={styles.formelements}>
            <GoABlock>
              <GoAFormItem
                label="First name"
                error={handleCheckErrors('ownerFirstName')}
              >
                <GoAInput
                  type="text"
                  width="100%"
                  maxLength={30}
                  name="ownerFirstName"
                  error={handleCheckErrors('ownerFirstName').length > 0}
                  value={vehicle.owner?.firstName ?? ''}
                  onChange={(_, value) => {
                    handleNestedChange(
                      ['owner', 'firstName'],
                      value,
                      setVehicle,
                    );
                  }}
                ></GoAInput>
              </GoAFormItem>
              <GoAFormItem
                label="Last name"
                error={handleCheckErrors('ownerLastName')}
              >
                <GoAInput
                  type="text"
                  width="100%"
                  maxLength={30}
                  name="ownerLastName"
                  value={vehicle.owner?.lastName ?? ''}
                  error={handleCheckErrors('ownerLastName').length > 0}
                  onChange={(_, value) => {
                    handleNestedChange(
                      ['owner', 'lastName'],
                      value,
                      setVehicle,
                    );
                  }}
                ></GoAInput>
              </GoAFormItem>
            </GoABlock>
            <GoASpacer vSpacing={'m'} />
            <GoAFormItem
              label="Street Address"
              error={handleCheckErrors('ownerStreetAddress')}
            >
              <GoAInput
                name="ownerStreetAddress"
                width="100%"
                maxLength={25}
                value={vehicle.owner?.address?.address ?? ''}
                error={handleCheckErrors('ownerStreetAddress').length > 0}
                onChange={(_, value) => {
                  handleNestedChange(
                    ['owner', 'address', 'address'],
                    value,
                    setVehicle,
                  );
                }}
              />
            </GoAFormItem>
            <GoASpacer vSpacing={'m'} />
            <GoAFormItem
              label="City or town"
              error={handleCheckErrors('ownerCity')}
            >
              <GoAInput
                name="ownerCity"
                width="100%"
                maxLength={25}
                value={vehicle.owner?.address?.city ?? ''}
                error={handleCheckErrors('ownerCity').length > 0}
                onChange={(_, value) => {
                  handleNestedChange(
                    ['owner', 'address', 'city'],
                    value,
                    setVehicle,
                  );
                }}
              />
            </GoAFormItem>
            <GoASpacer vSpacing={'m'} />
            <GoABlock gap={'xl'}>
              <GoAFormItem label="Province">
                <GoADropdown
                  name="ownerProvince"
                  disabled={true}
                  error={handleCheckErrors('province').length > 0}
                  width="190px"
                  value={
                    stateConfiguration.configuration.provinceOrState &&
                    stateConfiguration.configuration.provinceOrState.length > 0
                      ? stateConfiguration.configuration.provinceOrState
                          .find((x) => x.id.toString() == defaultProvince)!
                          .id.toString()
                      : ''
                  }
                  onChange={(_, value) => {
                    if (value) {
                      handleNestedChange(
                        ['owner', 'address', 'province'],
                        stateConfiguration.configuration.provinceOrState.find(
                          (x) => x.id.toString() == value,
                        ),
                        setVehicle,
                      );
                    }
                  }}
                >
                  {stateConfiguration.configuration.provinceOrState.map((x) => {
                    return (
                      <GoADropdownItem
                        value={x.id.toString()}
                        label={x.id.toString()}
                        key={x.id}
                      ></GoADropdownItem>
                    );
                  })}
                </GoADropdown>{' '}
              </GoAFormItem>
              <GoAFormItem
                label="Postal Code"
                error={handleCheckErrors('ownerPostalCode')}
              >
                <GoAInput
                  type="text"
                  width="100%"
                  maxLength={10}
                  name="ownerPostalCode"
                  value={vehicle.owner?.address?.postalCode ?? ''}
                  error={handleCheckErrors('ownerPostalCode').length > 0}
                  onChange={(_, value) => {
                    handleNestedChange(
                      ['owner', 'address', 'postalCode'],
                      value,
                      setVehicle,
                    );
                  }}
                />
              </GoAFormItem>
            </GoABlock>
            <GoASpacer vSpacing={'m'} />
            <GoABlock>
              <GoAFormItem
                label="Primary phone"
                error={handleCheckErrors('ownerPrimaryPhone')}
              >
                <GoAInput
                  type="text"
                  name="ownerPrimaryPhone"
                  maxLength={14}
                  width="100%"
                  placeholder="(xxx) xxx-xxxx"
                  value={vehicle.owner?.homePhoneNumber ?? ''}
                  error={handleCheckErrors('ownerPrimaryPhone').length > 0}
                  onChange={(_, value) => {
                    if (value) {
                      // Remove non-numeric characters for storage and reformat for display
                      const cleanedValue = value.replace(/\D/g, '');
                      const formattedValue = formatPhoneNumber(cleanedValue);
                      handleNestedChange(
                        ['owner', 'homePhoneNumber'],
                        formattedValue,
                        setVehicle,
                      );
                    } else
                      handleNestedChange(
                        ['owner', 'homePhoneNumber'],
                        value,
                        setVehicle,
                      );
                  }}
                />
              </GoAFormItem>
              <GoAFormItem
                label="Alternate Phone"
                error={handleCheckErrors('ownerAltPhone')}
              >
                <GoAInput
                  type="text"
                  name="ownerAltPhone"
                  maxLength={14}
                  width="100%"
                  value={vehicle.owner?.workPhoneNumber ?? ''}
                  placeholder="(xxx) xxx-xxxx"
                  error={handleCheckErrors('ownerAltPhone').length > 0}
                  onChange={(_, value) => {
                    if (value) {
                      // Remove non-numeric characters for storage and reformat for display
                      const cleanedValue = value.replace(/\D/g, '');
                      const formattedValue = formatPhoneNumber(cleanedValue);
                      handleNestedChange(
                        ['owner', 'workPhoneNumber'],
                        formattedValue,
                        setVehicle,
                      );
                    } else
                      handleNestedChange(
                        ['owner', 'workPhoneNumber'],
                        value,
                        setVehicle,
                      );
                  }}
                />
              </GoAFormItem>
            </GoABlock>
            <GoASpacer vSpacing={'2xl'} />
          </div>
        </div>
        <GoASpacer vSpacing={'l'} />
        <InsuranceInformationComponent
          vehicle={vehicle}
          stateUpdate={setVehicle}
          onChange={handleNestedChange}
          checkError={handleCheckErrors}
        />

        <WizardButtonGroup
          nextPage={nextPage}
          validate={() => {
            return validate();
          }}
          goToReview={navigationConfiguration.goToReview}
          save={save}
          navigate={navigationConfiguration.navigate}
        />
      </div>
    </div>
  );
};

interface VehicleInformationComponentProps {
  vehicle: Vehicle;
  isEcollisionInvalid?: boolean;
  stateUpdate: any;
  onChange: any;
  checkError: (fieldName: string) => string;
}
const VehicleInformationComponent = ({
  vehicle,
  isEcollisionInvalid = false,
  stateUpdate,
  onChange,
  checkError,
}: VehicleInformationComponentProps) => {
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const api = useCsrService();

  const [makes, setMakes] = useState<string[]>();
  const [models, setModels] = useState<string[]>();
  const [years, setYears] = useState<string[]>();

  useEffect(() => {
    const fetchMakes = async () => {
      const makes = await getMakes();
      setMakes(makes);
    };

    const createYears = () => {
      const currentYear = new Date().getFullYear();
      const maxYear = Math.max(currentYear + 2);
      const yearArray = [];

      for (let i = maxYear; i >= 1916; i--) {
        yearArray.push(i.toString());
      }

      yearArray.push('9999');

      setYears(yearArray);
    };

    fetchMakes();
    createYears();
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      const response = await api.get(`/api/v1/vehicle/${vehicle.make}/models`);
      if (response) {
        setModels(response.data.models);
      }
    };

    if (vehicle.make) {
      fetchModels();
    } else {
      setModels(undefined);
    }
  }, [vehicle.make]);

  const getMakes = async (): Promise<string[]> => {
    const response = await api.get('/api/v1/vehicle/makes');

    if (response) {
      return response.data.makes;
    }
    return Promise.reject();
  };

  return (
    <div>
      <div></div>
      <div className="large-body">Vehicle information</div>
      <GoASpacer vSpacing={'xl'} />
      <div style={{ display: displaySection(isEcollisionInvalid) }}>
        <GoACallout type="emergency" size="medium">
          One or more fields do not match our records. Verify your licence
          plate, VIN, make, model, and year before proceeding.
        </GoACallout>
      </div>
      <GoASpacer vSpacing={'xs'} />
      <GoABlock>
        <GoAFormItem
          label="Licence plate"
          error={checkError('licencePlateNumber')}
          helpText="for exmaple A1B 2C3"
        >
          <GoAInputText
            name="licencePlateNumber"
            width="100%"
            maxLength={25}
            error={
              checkError('licencePlateNumber').length > 0 || isEcollisionInvalid
            }
            value={vehicle.licencePlateNumber}
            onChange={(_, value) => {
              onChange(['licencePlateNumber'], value, stateUpdate);
            }}
          ></GoAInputText>
        </GoAFormItem>
        <GoAFormItem label="Province" error={checkError('province')}>
          <GoADropdown
            name="province"
            disabled={true}
            error={checkError('province').length > 0}
            width="190px"
            value={defaultProvince}
            relative={true}
            onChange={(_, value) => {
              if (value) {
                onChange(['driver', 'address', 'province'], value, stateUpdate);
              }
            }}
          >
            {stateConfiguration.configuration.provinceOrState &&
            stateConfiguration.configuration.provinceOrState.length > 0
              ? stateConfiguration.configuration.provinceOrState.map((x) => {
                  return (
                    <GoADropdownItem
                      value={x.id.toString()}
                      label={x.id.toString()}
                      key={x.id}
                    ></GoADropdownItem>
                  );
                })
              : ''}
          </GoADropdown>
        </GoAFormItem>
      </GoABlock>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem
        label="Vehicle identification number (VIN)"
        error={checkError('vinNumber')}
        helpText="for example 3FAHP0JG8AR389178"
      >
        <GoAInputText
          name="vin"
          value={vehicle.vinNumber}
          error={checkError('vinNumber').length > 0 || isEcollisionInvalid}
          width="100%"
          onChange={(_, value) => {
            const sanitizedValue = value
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, '');
            onChange(['vinNumber'], sanitizedValue, stateUpdate);
          }}
        ></GoAInputText>
      </GoAFormItem>
      <GoASpacer vSpacing={'m'} />
      <GoABlock>
        <GoAFormItem label="Year" error={checkError('year')}>
          <GoADropdown
            name="year"
            filterable={true}
            error={checkError('year').length > 0 || isEcollisionInvalid}
            width="190px"
            value={vehicle.year?.toString()}
            relative={true}
            leadingIcon="search"
            onChange={(_, value) => {
              onChange(['year'], value, stateUpdate);
            }}
          >
            {years
              ? years.map((year) => {
                  return (
                    <GoADropdownItem
                      value={year}
                      label={year}
                      key={year}
                    ></GoADropdownItem>
                  );
                })
              : ' '}
          </GoADropdown>
        </GoAFormItem>
        <GoAFormItem label="Make" error={checkError('make')}>
          <GoADropdown
            name="make"
            filterable={true}
            error={checkError('make').length > 0 || isEcollisionInvalid}
            width="190px"
            value={vehicle.make?.toString()}
            leadingIcon="search"
            relative={true}
            placeholder="--Select--"
            onChange={(_, value) => {
              onChange(['make'], value, stateUpdate);
            }}
          >
            {makes
              ? makes.map((make) => {
                  return (
                    <GoADropdownItem
                      value={make}
                      label={make}
                      mountType="reset"
                      key={make}
                    ></GoADropdownItem>
                  );
                })
              : ' '}
          </GoADropdown>
        </GoAFormItem>
      </GoABlock>
      <GoASpacer vSpacing={'m'} />
      <GoABlock>
        <GoAFormItem label="Model" error={checkError('model')}>
          <GoADropdown
            name="model"
            filterable={true}
            error={checkError('model').length > 0 || isEcollisionInvalid}
            width="190px"
            value={vehicle.model?.toString()}
            leadingIcon="search"
            relative={true}
            placeholder="--Select--"
            onChange={(_, value) => {
              onChange(['model'], value, stateUpdate);
            }}
          >
            {models
              ? models.map((model, index) => {
                  return (
                    <GoADropdownItem
                      value={model}
                      label={model}
                      mountType="reset"
                      key={`${model}_${index}_${Math.random()}`}
                    ></GoADropdownItem>
                  );
                })
              : ' '}
          </GoADropdown>
        </GoAFormItem>
        <GoAFormItem label="Vehicle type" error={checkError('type')}>
          <GoADropdown
            name="type"
            filterable={true}
            error={checkError('type').length > 0}
            width="190px"
            value={vehicle.type?.toString()}
            leadingIcon="search"
            relative={true}
            placeholder="--Select--"
            onChange={(_, value) => {
              onChange(['type'], value, stateUpdate);
            }}
          >
            {stateConfiguration.configuration.objectIdentification.map(
              (type) => {
                if (
                  type.value === 'Sedan' ||
                  type.value === 'Pick-Up/Van < 4500kg' ||
                  type.value == 'Mini-Van/MPV/SUV' ||
                  type.value == 'Motorhome'
                ) {
                  return (
                    <GoADropdownItem
                      value={type.id.toString()}
                      label={type.value}
                      key={type.id}
                    ></GoADropdownItem>
                  );
                }
              },
            )}
          </GoADropdown>
        </GoAFormItem>
      </GoABlock>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem label="Vehicle colour" error={checkError('color')}>
        <GoADropdown
          name="color"
          width="190px"
          filterable={true}
          value={vehicle.color?.toString()}
          relative={true}
          placeholder="--Select--"
          error={checkError('color').length > 0}
          onChange={(_, value) => {
            onChange(['color'], value, stateUpdate);
          }}
        >
          {stateConfiguration.configuration.vehicleColours.map((x) => {
            return (
              <GoADropdownItem
                value={x.id.toString()}
                label={x.value}
                key={x.id}
              ></GoADropdownItem>
            );
          })}
        </GoADropdown>
      </GoAFormItem>
    </div>
  );
};

interface InsuranceInformationComponentProps {
  vehicle: Vehicle;
  stateUpdate: any;
  onChange: any;
  checkError: (fieldName: string) => string;
}
const InsuranceInformationComponent = ({
  vehicle,
  stateUpdate,
  onChange,
  checkError,
}: InsuranceInformationComponentProps) => {
  return (
    <div>
      <GoASpacer vSpacing={'m'} />
      <div className="large-body">Damage estimate information</div>
      <div className={styles.formelements}>
        <GoASpacer vSpacing="m" />
        <GoAFormItem
          label="Have you obtained an estimate for the damage to your vehicle?"
          error={checkError('hasObtainedDamageEstimate')}
        >
          <GoARadioGroup
            name="hasObtainedDamageEstimate"
            value={
              vehicle.hasObtainedDamageEstimate === undefined ||
              vehicle.hasObtainedDamageEstimate === null
                ? ''
                : vehicle.hasObtainedDamageEstimate
                  ? 'Yes'
                  : 'No'
            }
            error={checkError('hasObtainedDamageEstimate').length > 0}
            onChange={(_, value) => {
              const hasObtainedDamageEstimate = value.toLowerCase() === 'yes';
              onChange(
                ['hasObtainedDamageEstimate'],
                hasObtainedDamageEstimate,
                stateUpdate,
              );
              if (!hasObtainedDamageEstimate) {
                onChange(['damageEstimate'], '0', stateUpdate);
              }
            }}
          >
            <GoARadioItem value="Yes" />
            <GoARadioItem value="No" />
          </GoARadioGroup>
        </GoAFormItem>

        <div
          style={{
            display:
              vehicle.hasObtainedDamageEstimate === undefined ||
              vehicle.hasObtainedDamageEstimate === null ||
              vehicle.hasObtainedDamageEstimate === false
                ? 'none'
                : 'block',
          }}
        >
          <GoAFormItem
            label="Enter the combined total (in dollars) of the estimated damage to your vehicle and all other property damage:"
            error={checkError('damageEstimate')}
          >
            <GoAInput
              leadingContent="$"
              name="damageEstimate"
              width="100%"
              error={checkError('damageEstimate').length > 0}
              value={
                vehicle.damageEstimate ? vehicle.damageEstimate.toString() : ''
              }
              onChange={(_, value) => {
                onChange(['damageEstimate'], value, stateUpdate);
              }}
            ></GoAInput>
          </GoAFormItem>
        </div>
        <GoASpacer vSpacing={'l'} />
      </div>
      <div className="large-body">Insurance information</div>
      <GoASpacer vSpacing={'m'} />
      <GoACallout type="information">
        The information below can be found on the vehicle's insurance
        certificate.
      </GoACallout>
      <div className={styles.formelements}>
        <GoAFormItem
          label="Insurance company"
          error={checkError('companyName')}
        >
          <GoAInputText
            name="companyName"
            maxLength={50}
            value={vehicle.insuredBy?.companyName ?? ''}
            error={checkError('companyName').length > 0}
            width="100%"
            onChange={(_, value) => {
              onChange(['insuredBy', 'companyName'], value, stateUpdate);
            }}
          ></GoAInputText>
        </GoAFormItem>
        <GoASpacer vSpacing={'m'} />
        <GoABlock>
          <GoAFormItem
            label="Policy number"
            error={checkError('insurancePolicyNumber')}
          >
            <GoAInput
              name="insurancePolicyNumber"
              maxLength={20}
              value={vehicle.insuredBy?.insurancePolicyNumber ?? ''}
              error={checkError('insurancePolicyNumber').length > 0}
              width="100%"
              onChange={(_, value) => {
                onChange(
                  ['insuredBy', 'insurancePolicyNumber'],
                  value,
                  stateUpdate,
                );
              }}
            ></GoAInput>
          </GoAFormItem>
          <GoAFormItem
            label="Expiry date"
            error={checkError('insurancePolicyExpirationDate')}
          >
            <GoAInput
              type="date"
              name="insurancePolicyExpirationDate"
              error={checkError('insurancePolicyExpirationDate').length > 0}
              value={
                vehicle.insuredBy?.insurancePolicyExpirationDate?.split('T')[0]
              }
              width="100%"
              onChange={(_, value) => {
                onChange(
                  ['insuredBy', 'insurancePolicyExpirationDate'],
                  value,
                  stateUpdate,
                );
              }}
            ></GoAInput>
          </GoAFormItem>
        </GoABlock>
      </div>
    </div>
  );
};
